<template>
  <el-scrollbar class="tabs">
    <div class="tabs-inner h-full flex">
      <div
        v-for="(v, idx) in tabList"
        :key="idx"
        class="tab-item"
        :data-name="v.name"
        :class="[
          { active: v.name === route.name },
          { prev: idx - currTabIdx === -1 },
          { next: idx - currTabIdx === 1 },
        ]"
      >
        <a
          @click="toPage(v, idx)"
        >
          {{ v.title }}
        </a>

        <svg-icon
          v-if="v.name !== indexPath.slice(1)"
          class="absolute top-[9px] right-[8px] !w-[18px] !h-[18px] text-[var(--color-text-primary)]"
          icon="iconamoon:close-thin"
          @click.prevent="() => closeTab(v.name)"
        />
      </div>

      <!-- 圆角修复 -->
      <div
        class="tab-item last !w-[10px]"
        :class="[
          { next: currTabIdx === tabsStore.tabList.length - 1 },
        ]"
      >
        &nbsp;
      </div>
    </div>
  </el-scrollbar>

  <!-- context menu -->
  <div
    v-show="contextMenuName !== ''"
    class="context-menu"
    :style="`top: ${xy.y}px; left: ${xy.x}px;`"
  >
    <a @click="relush">刷新</a>
    <a @click="closeTab(contextMenuName)">关闭</a>
    <a @click="closeOtherTabs">关闭其他</a>
    <a @click="tabsStore.removeAll">关闭全部</a>
  </div>
</template>

<script setup lang="ts">
import { SvgIcon } from '@vue3/components';
import { useRoute, useRouter } from 'vue-router';
import { computed, nextTick, onBeforeMount, onMounted, ref, toRaw, watch } from 'vue';
import { indexPath, ITab, tabState, useTabsStore } from '../..';

const route = useRoute();
const router = useRouter();

const tabsStore = useTabsStore();

const currTabIdx = ref(0);
const contextMenuName = ref('');
const xy = ref({
  x: 0,
  y: 0,
});

const tabList = computed(() => tabsStore.tabList);

const toPage = (v: ITab, idx) => {
  const { name, query, state } = v;

  if (name === route.name) return '';

  currTabIdx.value = idx;
  router.push({
    name,
    query,
    state: state ? JSON.parse(JSON.stringify(state)) : undefined,
  });
};

const relush = () => {
  const replaceIndex = tabsStore.tabList.findIndex((item) => item.name === contextMenuName.value);

  if (replaceIndex > -1) {
    tabState.data = toRaw(tabsStore.tabList[replaceIndex]);
    tabState.replaceIndex = replaceIndex;

    router.replace('/redirect');
    tabsStore.removeTab(contextMenuName.value, false);
  }
};

const closeOtherTabs = () => {
  tabsStore.removeOthers(contextMenuName.value);
};

const closeTab = (routePath: string) => {
  tabsStore.removeTab(routePath);
};

const contextMenuEvent = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();

  const { currentTarget } = e;
  // @ts-ignore
  const { pageX, pageY } = e;
  // @ts-ignore
  const { dataset } = currentTarget;
  const { name } = dataset;

  contextMenuName.value = name || '';
  xy.value = {
    x: pageX,
    y: pageY,
  };
};

const closeContextMenus = () => {
  contextMenuName.value = '';
};

watch(
  [tabList, route],
  () => {
    tabList.value.forEach((v, idx) => {
      if (v.name === route.name) {
        currTabIdx.value = idx;
      }
    });

    nextTick(() => {
      const tabs = document.querySelectorAll('.tab-item');

      if (tabs) {
        for (const v of tabs) {
          if (v.classList.contains('last')) continue;

          v.removeEventListener('contextmenu', contextMenuEvent);
          v.addEventListener('contextmenu', contextMenuEvent);
        }
      }
    });
  },
  {
    deep: true,
    immediate: true,
  },
);

onMounted(() => {
  document.addEventListener('click', closeContextMenus, false);
});

onBeforeMount(() => {
  const tabs = document.querySelectorAll('.tab-item');

  if (tabs) {
    for (const v of tabs) {
      v.removeEventListener('contextmenu', contextMenuEvent);
    }
  }

  document.removeEventListener('click', closeContextMenus, false);
});
</script>

<style lang="scss" scoped>
:deep(.el-scrollbar__view) {
  height: 100%;
}

.tabs {
  @apply flex items-center h-[44px] box-border bg-white relative overflow-x-auto;

  border-top: 1px var(--tabs-border-color) solid;

  &-inner {
    background-color: var(--color-gray-checked);
    white-space: nowrap;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      width: 100%;
      height: 20px;
      background-color: #fff;
      top: 0;
      left: 0;
    }
  }
}

.tab-item {
  @apply bg-white text-sm relative mt-[8px] select-none;

  a {
    @apply text-[13px] cursor-pointer select-none flex items-center w-full h-full box-border pl-[32px] pr-[32px] py-[3px];

    color: var(--color-text-secondary);
  }

  .svg-icon {
    @apply block cursor-pointer;
  }

  &::after {
    @apply absolute right-0 bottom-[10px] w-[1px] h-[12px];

    content: '';
    background-color: var(--tabs-border-color);
  }

  &.prev,
  &.active,
  &.last {
    &::after {
      background-color: transparent;
    }
  }

  &.prev {
    border-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.next {
    border-radius: 0;
    border-bottom-left-radius: 0;
  }

  &:hover {
    .svg-icon {
      @apply block;
    }
  }

  &.active {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    background-color: var(--color-gray-checked);

    a {
      @apply flex items-center;

      color: var(--color-primary);
    }

    &::before {
      content: '';
      background-color: var(--color-gray-checked);
    }
  }

  &:first-child {
    &.active {
      border-top-left-radius: 0;
    }
  }
}

.context-menu {
  @apply fixed flex flex-col bg-white z-50 py-1 rounded-sm;

  box-shadow: 0 0 5px #cfcece;

  a {
    @apply cursor-pointer text-[12px] text-[var(--color-text-primary)] py-2 px-4 hover:bg-gray-200;
  }
}

.hidden-tab {
  display: none !important;
}
</style>
